import { LRUCache } from '@meego/utils/src/dataStructures/LRUCache';

import { StorageType, LRULocalStorageConfig } from './type';
import { localStorage } from '.';

type ModeType = 'visit' | 'modify';

export class LRULocalStorage<T> {
  private resourceWithTime: { _t: number; r: T } | null;

  readonly resourceKey: string;

  readonly storageKey: keyof StorageType;

  readonly mode: ModeType;

  private readonly cache: LRUCache<string, { _t: number; r: T }>;

  /**
   * LRU LocalStorage 存储（淘汰策略，按激活时间，越远的越先被淘汰）
   *    激活时间：默认以修改时间作为激活时间（默认为修改时间），LRULocalStorageConfig 中配置
   *    缓存大小：允许最多存储的资源个数（默认 6），LRULocalStorageConfig 中配置
   *
   * @param props storageKey localStorage 缓存 Key
   * @param props resourceKey 资源 Key，最终存储格式为 storageKey: { [resourceKey]: resource }
   */
  constructor(props: { storageKey: keyof StorageType; resourceKey: string }) {
    const { storageKey, resourceKey } = props;

    this.mode = LRULocalStorageConfig[storageKey]?.mode || 'modify';
    this.storageKey = storageKey;
    this.resourceKey = resourceKey;

    const size = LRULocalStorageConfig[storageKey]?.size || 6;
    this.cache = new LRUCache<string, { _t: number; r: T }>(size);

    const localResourceMap = localStorage.getItem(storageKey) ?? {};
    this.resourceWithTime = localResourceMap[resourceKey];

    const values = Object.entries(localResourceMap)
      .filter(([_resourceKey, item]) => {
        const _item = item as unknown as { _t: number; r: T };
        // 时间戳不能为 0
        return _item && _item._t;
      })
      .sort((item1, item2) => item1[1]._t - item2[1]._t) as [string, { _t: number; r: T }][];

    values.forEach(([_resourceKey, _resourceWithTime]) => {
      this.cache.set(_resourceKey, _resourceWithTime);
    });
  }

  /**
   * 设置资源
   * @param resource
   */
  setResource(resource: T) {
    this.resourceWithTime = { _t: Date.now(), r: resource };
    this.updateLocalStorage();
  }

  /**
   * 获取资源
   */
  getResource() {
    if (this.mode === 'visit') {
      this.updateTime();
    }
    return this.resourceWithTime?.r;
  }

  /**
   * 清空资源
   */
  clearResource() {
    this.resourceWithTime = null;
    this.updateLocalStorage();
  }

  private updateTime() {
    if (this.resourceWithTime) {
      this.setResource(this.resourceWithTime?.r);
    }
  }

  private updateLocalStorage() {
    if (this.resourceWithTime) {
      this.cache.set(this.resourceKey, this.resourceWithTime);
    } else {
      this.cache.delete(this.resourceKey);
    }

    const obj = [...this.cache.keys()].reduce(
      (acc, resourceKey) => {
        acc[resourceKey] = this.cache.get(resourceKey);
        return acc;
      },
      {} as Record<string, T>,
    );

    localStorage.setItem(this.storageKey, obj);
  }
}
